import { z } from 'zod';

import { enforceRangePairs } from '@/lib/api/filter-spec';
import { pageMetaSchema } from '@/lib/api/pagination';

/** The four canonical Order Statuses (CONTEXT.md). `payment_failed` is intentionally absent —
 *  payment-failed orders are excluded server-side and never reach the listing. */
export const orderStatusSchema = z.enum(['order_placed', 'shipped', 'delivered', 'cancelled']);

export const orderRowSchema = z.object({
  id: z.string(),
  orderNumber: z.string(),
  customerId: z.string(),
  customerName: z.string(),
  customerAvatarUrl: z.string().nullable(),
  customerPhone: z.string(),
  orderAmount: z.number(),
  deviceCount: z.number(),
  status: orderStatusSchema,
  orderedAt: z.string(),
  deliveredAt: z.string().nullable(),
  city: z.string(),
});

export const orderListResponseSchema = z.object({
  data: z.array(orderRowSchema),
  meta: pageMetaSchema,
});

export const orderCustomerOptionSchema = z.object({ id: z.string(), name: z.string() });

// ── Order Detail (feature 102116) ─────────────────────────────────────────────

/** Installation request lifecycle (gates the order → Delivered transition). */
export const installationRequestStatusSchema = z.enum([
  'pending',
  'approved',
  'rejected',
  'cancelled',
]);

export const orderLineItemKindSchema = z.enum(['device', 'component', 'bundle']);

export const orderLineItemSchema = z.object({
  id: z.string(),
  kind: orderLineItemKindSchema,
  name: z.string(),
  imageUrl: z.string().nullable(),
  quantity: z.number(),
  unitPrice: z.number(),
  /** Placeholder render — owned by the Installation Request feature. */
  installationFee: z.number().nullable(),
  /** "N requested" badge; 0 hides it. */
  installationRequestCount: z.number(),
  /** "View Installation" link (inert placeholder). */
  hasInstallationRequest: z.boolean(),
  /** DM / CropX devices only — drives the "Add Parameter Template" affordance. */
  parameterApplicable: z.boolean(),
  parameterConfigured: z.boolean(),
  /** Sub-devices for a bundle row. */
  bundleDevices: z.array(z.object({ name: z.string(), quantity: z.number() })).nullable(),
});

export const orderTotalsSchema = z.object({
  orderPrice: z.number(),
  installationFees: z.number(),
  tax: z.number(),
  total: z.number(),
});

export const orderDetailCustomerSchema = z.object({
  id: z.string(),
  name: z.string(),
  phone: z.string(),
  email: z.string().nullable(),
  avatarUrl: z.string().nullable(),
});

export const orderMetaSchema = z.object({
  orderDate: z.string(), // YYYY-MM-DD; min bound for expected delivery
  paymentMethod: z.string(),
  paymentDate: z.string().nullable(),
  paymentComplete: z.boolean(), // gates the Invoice button
  transactionId: z.string(),
  expectedDelivery: z.string().nullable(), // YYYY-MM-DD; label flips to "Delivery Date" once delivered
});

export const orderLogisticsSchema = z.object({
  city: z.string(),
  billingAddress: z.string(),
  shippingAddress: z.string(),
});

export const orderDetailSchema = z.object({
  id: z.string(),
  orderNumber: z.string(),
  status: orderStatusSchema,
  customer: orderDetailCustomerSchema,
  lineItems: z.array(orderLineItemSchema),
  totals: orderTotalsSchema,
  orderMeta: orderMetaSchema,
  logistics: orderLogisticsSchema,
  installationRequest: z.object({ status: installationRequestStatusSchema }).nullable(),
  hasReview: z.boolean(),
  deliveredAt: z.string().nullable(), // actual delivery date once status === delivered
});

export const orderReviewSchema = z.object({
  lineItemId: z.string(),
  productName: z.string(),
  productImageUrl: z.string().nullable(),
  rating: z.number().min(0).max(5),
  comment: z.string().nullable(),
});

export const orderAssigneeOptionSchema = z.object({ id: z.string(), name: z.string() });
export const parameterTemplateOptionSchema = z.object({ id: z.string(), name: z.string() });

// ── Mutation payloads ─────────────────────────────────────────────────────────

export const raiseTicketInputSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  assigneeId: z.string().min(1, 'Assignee is required'),
  description: z.string().min(1, 'Description is required'),
});

export const requestReplacementInputSchema = z.object({
  lineItemId: z.string().min(1, 'Select a device'),
  reason: z.string().min(1, 'Reason is required'),
  imageUrls: z.array(z.string()),
});

export const addParametersInputSchema = z.object({
  entries: z.array(
    z.object({
      deviceId: z.string(),
      serialNumber: z.string().min(1, 'Serial number is required'),
      parameterTemplateId: z.string().min(1, 'Select a template'),
    }),
  ),
});

export const orderFiltersSchema = z
  .object({
    customerIds: z.array(z.string()),
    cities: z.array(z.string()),
    statuses: z.array(orderStatusSchema),
    orderedFrom: z.string().nullable(),
    orderedTo: z.string().nullable(),
    amountFrom: z.number().nullable(),
    amountTo: z.number().nullable(),
    deviceCountFrom: z.number().nullable(),
    deviceCountTo: z.number().nullable(),
  })
  .superRefine(
    enforceRangePairs([
      ['amountFrom', 'amountTo'],
      ['deviceCountFrom', 'deviceCountTo'],
    ]),
  )
  .superRefine((v, ctx) => {
    if (v.orderedFrom && v.orderedTo && v.orderedFrom > v.orderedTo) {
      ctx.addIssue({
        code: 'custom',
        path: ['orderedTo'],
        message: 'From date cannot be after To date',
      });
    }
  });
